import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  Gamepad2, TrendingUp, Bitcoin, Gem, Car, Anchor, 
  Ship, ShoppingCart, Briefcase, Cloud, Heart, Cpu 
} from 'lucide-react';

const industries = [
  { 
    name: 'Gaming & Gambling', 
    icon: Gamepad2,
    description: 'Seamless deposits, withdrawals, and multi-currency payouts for gaming operators.'
  },
  { 
    name: 'CFD / FX', 
    icon: TrendingUp,
    description: 'Real-time fund transfers and margin management for trading platforms.'
  },
  { 
    name: 'Crypto', 
    icon: Bitcoin,
    description: 'Fiat on/off ramps and compliant payment rails for digital asset businesses.'
  },
  { 
    name: 'Luxury Goods', 
    icon: Gem,
    description: 'High-value transaction processing with enhanced security protocols.'
  },
  { 
    name: 'Luxury Cars', 
    icon: Car,
    description: 'Large-sum transfers and international payment solutions for automotive.'
  },
  { 
    name: 'Luxury Yachts', 
    icon: Anchor,
    description: 'Cross-border payments and escrow services for maritime transactions.'
  },
  { 
    name: 'Shipping & Logistics', 
    icon: Ship,
    description: 'Multi-currency settlements and supply chain payment automation.'
  },
  { 
    name: 'E-commerce', 
    icon: ShoppingCart,
    description: 'Scalable checkout solutions with global payment method coverage.'
  },
  { 
    name: 'Professional Services', 
    icon: Briefcase,
    description: 'Invoice payments, retainers, and international fee collection.'
  },
  { 
    name: 'SaaS', 
    icon: Cloud,
    description: 'Subscription billing, usage-based payments, and revenue automation.'
  },
  { 
    name: 'Healthcare', 
    icon: Heart,
    description: 'Secure patient payments and medical billing integrations.'
  },
  { 
    name: 'Fintech', 
    icon: Cpu,
    description: 'White-label infrastructure and embedded finance solutions.'
  }
];

export const Industries = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="industries" 
      className="py-16 lg:py-20 bg-white border-t border-[#E5E7EB]"
      data-testid="industries-section"
    >
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10"
        >
          <span className="label-text text-[#6B7280] block mb-4">
            Industries
          </span>
          <h2 
            className="heading-lg text-3xl md:text-4xl lg:text-5xl text-[#1E3A5F] mb-4"
            data-testid="industries-title"
          >
            Industries We Serve
          </h2>
          <p className="text-lg text-[#6B7280] max-w-2xl">
            Tailored payment infrastructure for specialized sectors requiring compliance expertise and reliable transaction processing.
          </p>
        </motion.div>

        {/* Industries Grid */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          data-testid="industries-grid"
        >
          {industries.map((industry, index) => (
            <motion.div
              key={industry.name}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: Math.min(index * 0.05, 0.5), 
                ease: [0.22, 1, 0.36, 1] 
              }}
              className="group relative bg-[#F9FAFB] border border-[#E5E7EB] p-6 lg:p-8 hover:bg-[#1E3A5F] hover:border-[#0A0A0A] transition-all duration-300 cursor-pointer"
              data-testid={`industry-${index}`}
            >
              {/* Icon */}
              <div className="mb-4">
                <industry.icon 
                  className="w-8 h-8 text-[#1E3A5F] group-hover:text-white transition-colors duration-300" 
                  strokeWidth={1.25} 
                />
              </div>
              
              {/* Industry Name */}
              <h3 className="text-lg font-semibold text-[#1E3A5F] group-hover:text-white mb-3 transition-colors duration-300">
                {industry.name}
              </h3>
              
              {/* Description */}
              <p className="text-sm text-[#6B7280] group-hover:text-white/70 leading-relaxed transition-colors duration-300">
                {industry.description}
              </p>
              
              {/* Hover Arrow */}
              <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <svg 
                  className="w-5 h-5 text-white" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 17L17 7M17 7H7M17 7V17" />
                </svg>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
